import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useAlert } from "../../../providers/AlertProvider";

import { WarehousePageFunctions } from "../../../functions/warehouse_page/warehouse_page_functions";


const HistoricalWarehouseData = ({
    theme,
    show,
    onHide,
    customerId,
}) => {

    const { showAlert } = useAlert();
    const warehouseFunctions = new WarehousePageFunctions(showAlert);

    const [stockDate, setStockDate] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const isDark = theme === 'dark';

    // ---------------------------------------------------------
    // Colour scheme
    // ---------------------------------------------------------

    const backgroundColor = isDark ? '#343a40' : '#ffffff';
    const textColor = isDark ? '#f8f9fa' : '#212529';
    const mutedColor = isDark ? '#adb5bd' : '#6c757d';
    const borderColor = isDark ? '#ebebeb' : '#353636';
    const inputBackground = isDark ? '#2b3035' : '#ffffff';
    const headerBackground = isDark ? '#2b3035' : '#f8f9fa';
    const footerBackground = isDark ? '#2b3035' : '#f8f9fa';

    const handleDownload = async () => {
        setError('');

        if (!customerId) {
            setError('Please select a customer first.');
            return;
        }

        if (!stockDate) {
            setError('Please select a date.');
            return;
        }

        setIsLoading(true);

        try {
            const result =
                await warehouseFunctions.generateHistoricalStockReport(
                    customerId,
                    stockDate
                );

            if (!result.success) {
                setError(
                    result.message ||
                    'Unable to generate historical stock report.'
                );
                return;
            }

            setStockDate('');
            setError('');
            onHide();

        } catch (err) {
            console.error(
                'Historical stock report error:',
                err
            );

            setError(
                err.message ||
                'Unable to generate historical stock report.'
            );

        } finally {
            setIsLoading(false);
        }
    };


    const handleClose = () => {
        if (isLoading) return;

        setError('');
        setStockDate('');
        onHide();
    };


    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            backdrop="static"
            style={{
                zIndex: 1060,
            }}
            contentClassName={isDark ? 'bg-dark text-light' : ''}
        >
            <div
                style={{
                    border: `4px solid ${borderColor}`,
                    backgroundColor: backgroundColor,
                    borderRadius: '8px'
                }}
            >
                {/* -------------------------------------------------
                    Header
                ------------------------------------------------- */}

                <Modal.Header
                    style={{
                        backgroundColor: headerBackground,
                        color: textColor,
                        border: `1px solid ${borderColor}`
                    }}
                >

                    <Modal.Title
                        style={{
                            color: textColor,
                            fontWeight: 600
                        }}
                    >
                        Historical Warehouse Data
                    </Modal.Title>

                </Modal.Header>


                {/* -------------------------------------------------
                    Body
                ------------------------------------------------- */}

                <Modal.Body
                    style={{
                        backgroundColor,
                        color: textColor
                    }}
                >

                    {error && (
                        <Alert
                            variant="danger"
                            className="mb-3"
                        >
                            {error}
                        </Alert>
                    )}


                    <Form>

                        {/* Customer */}

                        <Form.Group className="mb-3">

                            <Form.Label
                                style={{
                                    color: textColor,
                                    fontWeight: 500
                                }}
                            >
                                Customer ID
                            </Form.Label>

                            <Form.Control
                                type="text"
                                value={customerId || ''}
                                disabled
                                style={{
                                    backgroundColor: inputBackground,
                                    color: textColor,
                                    borderColor,
                                    opacity: 1
                                }}
                            />

                        </Form.Group>


                        {/* Stock date */}

                        <Form.Group>

                            <Form.Label
                                style={{
                                    color: textColor,
                                    fontWeight: 500
                                }}
                            >
                                Stock Position Date
                            </Form.Label>

                            <Form.Control
                                type="date"
                                value={stockDate}
                                onChange={(e) =>
                                    setStockDate(e.target.value)
                                }
                                max={
                                    new Date()
                                        .toISOString()
                                        .split('T')[0]
                                }
                                disabled={isLoading}
                                style={{
                                    backgroundColor: inputBackground,
                                    color: textColor,
                                    borderColor,
                                    colorScheme: isDark
                                        ? 'dark'
                                        : 'light'
                                }}
                            />

                            <Form.Text
                                style={{
                                    color: mutedColor
                                }}
                            >
                                The warehouse stock position will
                                be reconstructed as at the end of
                                this date.
                            </Form.Text>

                        </Form.Group>

                    </Form>

                </Modal.Body>


                {/* -------------------------------------------------
                    Footer
                ------------------------------------------------- */}

                <Modal.Footer
                    style={{
                        backgroundColor: footerBackground,
                        borderTop: `1px solid ${borderColor}`
                    }}
                >

                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>


                    <Button
                        variant="primary"
                        onClick={handleDownload}
                        disabled={
                            isLoading ||
                            !stockDate ||
                            !customerId
                        }
                    >

                        {isLoading ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />

                                Generating...
                            </>
                        ) : (
                            'Download Historical Data'
                        )}

                    </Button>

                </Modal.Footer>
            </div>
        </Modal>
    );
};

export default HistoricalWarehouseData;